import { mediaList } from '@/mocks/media';
import { newsList } from '@/mocks/news';
import type { Media, News } from '@/types/media';

export const GENRES = ['科幻', '悬疑', '犯罪', '动作', '冒险', '爱情', '剧情', '喜剧', '动画', '奇幻', '恐怖', '武侠', '战争', '古装', '都市', '历史', '医疗', '运动', '青春', '家庭'];
export const REGIONS = ['美国', '中国', '日本', '韩国', '英国', '法国'];
export const YEARS = [2025, 2024, 2023, 2022, 2021];
export const TV_STATUS = ['更新中', '已完结'];
export const SORT_OPTIONS = [
  { value: 'hot', label: '最热' },
  { value: 'new', label: '最新' },
  { value: 'rating', label: '评分最高' },
];

export interface MediaFilters {
  genre?: string;
  region?: string;
  year?: string;
  status?: string;
  sort?: string;
}

export function getMediaByType(type: 'movie' | 'tv'): Media[] {
  return mediaList.filter((item) => item.type === type);
}

export function getMediaById(id: string): Media | undefined {
  return mediaList.find((item) => item.id === id);
}

export function getNewsById(id: string): News | undefined {
  return newsList.find((item) => item.id === id);
}

function parseViews(value: string): number {
  if (!value) return 0;
  if (value.includes('亿')) return parseFloat(value) * 10000;
  if (value.includes('万')) return parseFloat(value);
  return parseFloat(value);
}

export function filterMedia(type: 'movie' | 'tv', filters: MediaFilters): Media[] {
  let list = getMediaByType(type);

  if (filters.genre && filters.genre !== 'all') {
    list = list.filter((item) => item.genres.includes(filters.genre as string));
  }
  if (filters.region && filters.region !== 'all') {
    list = list.filter((item) => item.region === filters.region);
  }
  if (filters.year && filters.year !== 'all') {
    list = list.filter((item) => String(item.year) === filters.year);
  }
  if (type === 'tv' && filters.status && filters.status !== 'all') {
    list = list.filter((item) => item.status === filters.status);
  }

  const sort = filters.sort || 'hot';
  list = [...list].sort((a, b) => {
    if (sort === 'new') return b.year - a.year;
    if (sort === 'rating') return b.rating - a.rating;
    return parseViews(b.views) - parseViews(a.views);
  });

  return list;
}

export function getRelated(item: Media, limit = 6): Media[] {
  const related = mediaList.filter(
    (m) =>
      m.id !== item.id &&
      (m.genres.some((g) => item.genres.includes(g)) || m.region === item.region),
  );
  return related.slice(0, limit);
}

export function getHotMedia(limit = 6): Media[] {
  return [...mediaList].sort((a, b) => b.rating - a.rating).slice(0, limit);
}

export interface SearchResult {
  movies: Media[];
  tvs: Media[];
  news: News[];
}

export function searchAll(keyword: string): SearchResult {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return { movies: [], tvs: [], news: [] };

  const matchMedia = (item: Media) =>
    item.title.toLowerCase().includes(kw) ||
    item.genres.some((g) => g.toLowerCase().includes(kw)) ||
    item.actors.some((a) => a.toLowerCase().includes(kw)) ||
    item.director.toLowerCase().includes(kw);

  const matched = mediaList.filter(matchMedia);
  return {
    movies: matched.filter((m) => m.type === 'movie'),
    tvs: matched.filter((m) => m.type === 'tv'),
    news: newsList.filter(
      (n) => n.title.toLowerCase().includes(kw) || n.summary.toLowerCase().includes(kw) || n.category.toLowerCase().includes(kw),
    ),
  };
}

export function getPagedList<T>(list: T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

export function getTotalPages(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}